import { ReviewState, Task, TaskPrompt } from "@/api/types";
import AppButton from "../structure/AppButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faClock,
  faComments,
  faEdit,
  faMagnifyingGlass,
  faMessage,
  faUser,
  IconLookup,
} from "@fortawesome/free-solid-svg-icons";
import { useMemo } from "react";
import { timeAgo } from "@/utils/datetime";

interface TaskTileProps {
  task: Task;
  skipDependencyCheck?: boolean; // If true, ignore dependency status and always allow responding
  showEngagement?: boolean; // Whether to show engagement stats
  showUserProgress?: boolean; // Whether to show user's progress on the task
  children?: React.ReactNode; // Optional children to override default buttons/info
}

/**
 * A badge component to display engagement metrics with an icon and label.
 * 
 * Usage:
 * <EngagementNumberBadge icon={faEdit} label="submission" metric={5} color="teal" />
 * <EngagementNumberBadge icon={faCheck} label="participant completion" color="yellow">
 *   75% participant completion
 * </EngagementNumberBadge>
 * 
 * Props:
 * - icon: FontAwesome icon to display
 * - label: Label for the metric
 * - metric: Numeric value to display (optional if children provided)
 * - color: Tailwind color name (e.g. "teal", "green", "purple", "yellow")
 * - children: Optional custom content instead of metric + label
 *
 */
// we hardcode these classes because tailwind's bundler will elide classes that
// don't occur verbatim in the source code; that is, you can't construct a class
// name dynamically and expect it to be included in the build.
const BadgeColorMap = {
  teal: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300",
  green: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  purple: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  yellow: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
}
const EngagementNumberBadge = ({
  icon,
  label,
  metric,
  color,
  children
}: {
  icon: IconLookup;
  label: string;
  metric?: number;
  color: keyof typeof BadgeColorMap;
  children?: React.ReactNode;
}) => (
    <div className={`items-center px-2 py-1 rounded-full text-xs font-medium ${BadgeColorMap[color]}`}>
      <FontAwesomeIcon icon={icon} className="text-sm mr-1 w-4" />
      { children ? children : (
        <>
        {metric} {label}{metric !== 1 ? "s" : ""}
        </>
      )}
    </div>
);

export default function TaskTile({
  task,
  skipDependencyCheck = false,
  showEngagement = true,
  showUserProgress = true,
  children,
}: TaskTileProps) {
  
  // Format the number of prompts
  const promptCount = task.prompts?.length || 0;
  const promptText = promptCount === 1 ? "prompt" : "prompts";

  const defaultChildren = task.lastResponse ? (
    <>
      {/* Badges */}
      <div className="mb-3">
        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
          <FontAwesomeIcon icon={faCheck} className="text-sm mr-1" />
          Responded {timeAgo.format(new Date(task.lastResponse.createdAt!))}
        </div>
      </div>

      { /* Action buttons based on review state */ }
      {(() => {
        switch(task.lastResponse.reviewStatus) {
        case ReviewState.NeedsReview:
          return (
            <AppButton
              type="button"
              variant="secondary"
              forwardArrow={true}
              href={`/tasks/${task.id}/review`}
            >
              <FontAwesomeIcon icon={faMessage} className="text-xl mr-2" />
              Do Review
            </AppButton>
          );
        case ReviewState.Approved:
          if (task?.isDiscussable) {
            return (
              <AppButton
                type="button"
                variant="success"
                forwardArrow={true}
                href={`/tasks/${task.id}/discuss`}
              >
                <FontAwesomeIcon icon={faComments} className="text-xl mr-2" />
                Discuss Task
              </AppButton>
            );
          }
          else {
            return (
              <div className="text-gray-400 text-sm">
                No further action needed. Task is complete.
              </div>
            );
          }

        case ReviewState.Pending:
        default:
          return (
            <AppButton type="reset" variant="default" disabled={true}>
              <FontAwesomeIcon icon={faClock} className="text-xl mr-2" />
              Awaiting Review
            </AppButton>
          );
        }
      })()}
    </>
  ) : (
    <AppButton variant="primary" forwardArrow={true} href={`/tasks/${task.id}`}>
      <FontAwesomeIcon icon={faEdit} className="text-xl mr-2" /> Start Task
    </AppButton>
  );

  const canUserRespond = useMemo(() => {
    return skipDependencyCheck || task.userDependencyStatus?.canRespond;
  }, [task]);

  return (
    <div
      className={`mb-4 flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm lg:flex-row hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 ${
        !canUserRespond ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-center justify-center bg-blue-400 text-blue-100 text-6xl rounded-t-lg h-48 lg:h-auto lg:w-48 shrink-0 lg:rounded-none lg:rounded-s-lg">
        <FontAwesomeIcon
          icon={{ prefix: "fas", iconName: (task.icon || "clipboard") } as IconLookup}
        />
      </div>

      <div className="flex grow flex-col justify-between p-4 leading-normal">
        <div>
          <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {task.title}
          </h5>

          {task.description && (
            <p className="mb-3 font-normal text-gray-700 dark:text-gray-400">
              {task.description}
            </p>
          )}

          {/* Task Completion Stages */}
          {showUserProgress && canUserRespond && (
            <div className="mb-3 shrink lg:w-md xl:w-full">
              <div className="flex shrink items-center justify-between mb-4">
                {/* Stage 1: Not started */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-4 h-4 rounded-full bg-blue-500`}
                  ></div>
                </div>

                {/* line from 1 to 2 */}
                <div
                  className={`flex-1 h-0.5 ${
                    task.lastResponse ? "bg-blue-500" : "bg-gray-300"
                  }`}
                ></div>

                {/* Stage 2: Responded, under review */}
                {
                  task.isReviewable &&
                  <div className="flex flex-col items-center">
                  <div
                    className={`w-4 h-4 rounded-full ${
                      (task.lastResponse)
                        ? "bg-blue-500"
                        : "bg-gray-300"
                    }`}
                  ></div>
                </div>
                }

                {/* line from 2 to 3 */}
                <div
                  className={`flex-1 h-0.5 ${
                    task.lastResponse && task.lastResponse?.reviewStatus == ReviewState.Approved
                      ? "bg-blue-500"
                      : "bg-gray-300"
                  }`}
                ></div>

                {/* Stage 3: Reviewed, under discussion */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-4 h-4 rounded-full ${
                      task.lastResponse && task.lastResponse?.reviewStatus == ReviewState.Approved
                        ? "bg-blue-500"
                        : "bg-gray-300"
                    }`}
                  ></div>
                </div>
              </div>

              <div className="flex shrink justify-between text-xs text-gray-600 w-full mb-4">
                <div className="w-50">Not started</div>
                { task?.isReviewable && <div className="w-50 text-center">Under review</div> }
                <div className="w-50 text-right">{
                  task?.isDiscussable ? "Discussion" : "Complete"
                }</div>
              </div>
            </div>
          )}

          {/* <div className="mb-3 text-sm text-gray-600 dark:text-gray-500">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
              {promptCount} {promptText}
            </span>
          </div> */}

          <div className="mb-3 text-sm text-gray-600 dark:text-gray-500">
            {/* {task.dependencies && task.dependencies.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 ml-2">
                {task.dependencies.length} dependenc{task.dependencies.length !== 1 ? 'ies' : 'y'}
              </span>
            )}
            
            {canUserRespond && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 ml-2">
                Ready to respond
              </span>
            )}
            {!canUserRespond && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 ml-2">
                {task.userDependencyStatus.unmetDependencies.length} unmet dependenc{task.userDependencyStatus.unmetDependencies.length !== 1 ? 'ies' : 'y'}
              </span>
            )} */}

            {/* If we can't respond, show each unmet dependencies' title */}
            {!canUserRespond &&
              task.userDependencyStatus!.unmetDependencies.length > 0 &&
              task.userDependencyStatus!.unmetDependencies.map((dep) => (
                <span
                  key={`dep-${dep.taskId}`}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 mr-2"
                  title={dep.title} // show full title on hover
                >
                  Unmet dependency:{" "}
                  {dep.title && dep.title.length > 20
                    ? dep.title.slice(0, 17) + "..."
                    : dep.title}
                </span>
              ))}
          </div>
        </div>

        <div>{canUserRespond && (children || defaultChildren)}</div>
      </div>

      {/* Facilitator engagement stats; displayed to the right or bottom when narrow */}
      {showEngagement && task.engagementStats && (
        <div className="flex flex-col items-start p-4 leading-normal justify-self-end bg-gray-100 lg:w-60 lg:inset-shadow-left md:inset-shadow-top">
          <h3 className="text-md mb-3 font-bold">Engagement Statistics</h3>

          <div className="text-sm text-gray-600 dark:text-gray-500 space-y-2">
            <EngagementNumberBadge
              icon={faEdit}
              label="submission"
              metric={task.engagementStats.totalResponses}
              color="teal"
            />

            <EngagementNumberBadge
              icon={faComments}
              label="comment"
              metric={task.engagementStats.totalComments}
              color="green"
            />

            <EngagementNumberBadge
              icon={faUser}
              label="unique respondent"
              metric={task.engagementStats.numUniqueRespondents}
              color="purple"
            />

            <EngagementNumberBadge
              icon={faCheck}
              label="participant completion"
              color="yellow"
            >
              {task.engagementStats.userCompletionPercent.toFixed(0)}% participant completion
            </EngagementNumberBadge>
          </div>
        </div>
      )}
    </div>
  );
}
